import rembrandtsPrimaryUrl from '@/assets/fonts/rembrandts-primary.woff2';
import rembrandtsSecondaryUrl from '@/assets/fonts/rembrandts-secondary.woff2';
import artisanLogoUrl from '@/assets/images/by-theartisan.svg';

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const SKIN_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const ASSET_KEY_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._/-]{0,510}[a-zA-Z0-9]$/;
const ASSET_URL_PREFIX = '/api/v1/content/greenscreen/assets/';

interface UnknownRecord extends Record<string, unknown> {
  assets?: unknown;
  colors?: unknown;
  enabled?: unknown;
  id?: unknown;
  qr?: unknown;
  schema_version?: unknown;
  typography?: unknown;
}

export interface SessionPassTheme {
  id: string;
  logoUrl: string;
  wallpaperUrl?: string;
  background: string;
  surface: string;
  surfaceStrong: string;
  text: string;
  textMuted: string;
  accent: string;
  accentSecondary: string;
  onAccent: string;
  qrDark: string;
  qrLight: string;
  displayFont: string;
  bodyFont: string;
  fontFaces?: SessionPassFontFace[];
}

export interface SessionPassFontFace {
  family: string;
  url: string;
  style: 'normal' | 'italic';
  weight: string;
}

const ARTISAN_THEME: SessionPassTheme = {
  id: 'theartisan',
  logoUrl: artisanLogoUrl,
  background: '#f2f0ea',
  surface: '#ffffff',
  surfaceStrong: '#e5e2da',
  text: '#101010',
  textMuted: '#595959',
  accent: '#003ab5',
  accentSecondary: '#003ab5',
  onAccent: '#ffffff',
  qrDark: '#101010',
  qrLight: '#ffffff',
  displayFont: 'Arial, sans-serif',
  bodyFont: 'Arial, sans-serif',
};

const BUBBLE_PLANET_THEME: SessionPassTheme = {
  ...ARTISAN_THEME,
  id: 'bubble-planet',
  background: '#f4e8ce',
  surfaceStrong: '#f1e7f6',
  text: '#2d2038',
  textMuted: '#675d70',
  accent: '#5e3fb6',
  accentSecondary: '#ffa5c2',
  qrDark: '#5e3fb6',
};

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeColor(value: unknown): string | undefined {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value)
    ? value.toLowerCase()
    : undefined;
}

function linearizedChannel(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.04045
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(color: string): number {
  return (
    0.2126 * linearizedChannel(Number.parseInt(color.slice(1, 3), 16)) +
    0.7152 * linearizedChannel(Number.parseInt(color.slice(3, 5), 16)) +
    0.0722 * linearizedChannel(Number.parseInt(color.slice(5, 7), 16))
  );
}

function contrastRatio(first: string, second: string): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}

function resolveColors(value: unknown): Partial<SessionPassTheme> {
  if (!isRecord(value)) {
    return {};
  }
  const background = normalizeColor(value.background);
  const surface = normalizeColor(value.surface);
  const surfaceStrong = normalizeColor(value.surface_strong);
  const text = normalizeColor(value.text);
  const textMuted = normalizeColor(value.text_muted);
  const accent = normalizeColor(value.accent);
  const accentSecondary = normalizeColor(value.accent_secondary);
  const onAccent = normalizeColor(value.on_accent);
  const success = normalizeColor(value.success);
  const error = normalizeColor(value.error);
  if (
    !(
      background &&
      surface &&
      surfaceStrong &&
      text &&
      textMuted &&
      accent &&
      accentSecondary &&
      onAccent &&
      success &&
      error
    ) ||
    contrastRatio(text, background) < 4.5 ||
    contrastRatio(text, surface) < 4.5 ||
    contrastRatio(text, surfaceStrong) < 4.5 ||
    contrastRatio(textMuted, surface) < 4.5 ||
    contrastRatio(onAccent, accent) < 4.5
  ) {
    return {};
  }
  return {
    background,
    surface,
    surfaceStrong,
    text,
    textMuted,
    accent,
    accentSecondary,
    onAccent,
  };
}

function resolveAssetKey(
  value: unknown,
  skinId: string,
  allowedExtensions: ReadonlySet<string>
): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const key = value.trim();
  const prefix = `skins/${skinId}/`;
  if (
    !(
      ASSET_KEY_PATTERN.test(key) &&
      key.startsWith(prefix) &&
      /^v[1-9][0-9]*\//.test(key.slice(prefix.length))
    ) ||
    key.includes('..') ||
    key.includes('//') ||
    key.includes('\\') ||
    key.includes('%') ||
    key.includes('?') ||
    key.includes('#')
  ) {
    return undefined;
  }
  return allowedExtensions.has(key.slice(key.lastIndexOf('.')).toLowerCase())
    ? key
    : undefined;
}

function resolveTypography(
  value: unknown,
  skinId: string
): Partial<SessionPassTheme> {
  if (!isRecord(value)) {
    return {};
  }
  if (value.preset === 'rembrandts-amsterdam') {
    return {
      displayFont: '"Rembrandts Primary", Georgia, serif',
      bodyFont: '"Rembrandts Secondary", Georgia, serif',
      fontFaces: [
        {
          family: 'Rembrandts Primary',
          url: rembrandtsPrimaryUrl,
          weight: '100 900',
          style: 'normal',
        },
        {
          family: 'Rembrandts Secondary',
          url: rembrandtsSecondaryUrl,
          weight: '100 900',
          style: 'normal',
        },
      ],
    };
  }
  if (
    value.preset !== undefined ||
    !isRecord(value.families) ||
    !isRecord(value.roles)
  ) {
    return {};
  }
  const entries = Object.entries(value.families);
  if (entries.length === 0 || entries.length > 3) {
    return {};
  }
  const families = new Map<string, string>();
  const fontFaces: SessionPassFontFace[] = [];
  for (const [id, family] of entries) {
    if (
      !(
        /^[a-z][a-z0-9_-]{0,31}$/.test(id) &&
        isRecord(family) &&
        ['sans-serif', 'serif', 'monospace'].includes(
          String(family.fallback)
        ) &&
        Array.isArray(family.faces) &&
        family.faces.length > 0
      ) ||
      fontFaces.length + family.faces.length > 12
    ) {
      return {};
    }
    const name = `SessionSkin-${skinId}-${id}`;
    const descriptors = new Set<string>();
    for (const face of family.faces) {
      if (
        !(isRecord(face) && Number.isInteger(face.weight)) ||
        Number(face.weight) < 100 ||
        Number(face.weight) > 900 ||
        Number(face.weight) % 100 !== 0 ||
        (face.style !== 'normal' && face.style !== 'italic')
      ) {
        return {};
      }
      const key = resolveAssetKey(face.asset_key, skinId, new Set(['.woff2']));
      const fontPath = key?.slice(`skins/${skinId}/`.length);
      const descriptor = `${face.weight}:${face.style}`;
      if (
        !(
          key &&
          /^v[1-9][0-9]*\/fonts\/[a-zA-Z0-9][a-zA-Z0-9._-]{0,126}\.woff2$/.test(
            fontPath ?? ''
          )
        ) ||
        descriptors.has(descriptor)
      ) {
        return {};
      }
      descriptors.add(descriptor);
      fontFaces.push({
        family: name,
        url: `${ASSET_URL_PREFIX}${key}`,
        weight: String(face.weight),
        style: face.style,
      });
    }
    families.set(id, `"${name}", ${family.fallback}`);
  }
  const roles = value.roles;
  if (
    !['brand', 'display', 'body'].every(
      (role) => typeof roles[role] === 'string' && families.has(roles[role])
    )
  ) {
    return {};
  }
  return {
    displayFont: families.get(String(roles.display)),
    bodyFont: families.get(String(roles.body)),
    fontFaces,
  };
}

export function resolveSessionPassTheme(
  styleProfile: unknown,
  baseTheme?: string
): SessionPassTheme {
  const fallback =
    baseTheme === 'bubble-planet' ? BUBBLE_PLANET_THEME : ARTISAN_THEME;
  if (
    !isRecord(styleProfile) ||
    styleProfile.schema_version !== 1 ||
    typeof styleProfile.id !== 'string' ||
    !SKIN_ID_PATTERN.test(styleProfile.id) ||
    styleProfile.enabled === false
  ) {
    return { ...fallback };
  }

  const skinId = styleProfile.id;
  const assets = isRecord(styleProfile.assets) ? styleProfile.assets : {};
  const logoKey = resolveAssetKey(
    assets.logo_asset_key,
    skinId,
    new Set(['.png', '.webp'])
  );
  const wallpaperKey = resolveAssetKey(
    assets.wallpaper_asset_key,
    skinId,
    new Set(['.avif', '.jpeg', '.jpg', '.png', '.webp'])
  );
  const qr = isRecord(styleProfile.qr) ? styleProfile.qr : {};
  const qrDark = normalizeColor(qr.dark_color);
  const qrLight = normalizeColor(qr.light_color);
  const qrColors =
    qrDark && qrLight && contrastRatio(qrDark, qrLight) >= 4.5
      ? { qrDark, qrLight }
      : {};

  return {
    ...fallback,
    id: skinId,
    ...resolveColors(styleProfile.colors),
    ...qrColors,
    ...(logoKey ? { logoUrl: `${ASSET_URL_PREFIX}${logoKey}` } : {}),
    ...(wallpaperKey
      ? { wallpaperUrl: `${ASSET_URL_PREFIX}${wallpaperKey}` }
      : {}),
    ...resolveTypography(styleProfile.typography, skinId),
  };
}
